import React, { useState } from 'react';
import { Eye, Heart, Shield, Sparkles, Moon, Sun, Star, Flame, Wind, Zap, CircleDot, Target, Compass, Crown, Gem, Wand2, Ghost, Skull, TreePine, Flower2, Bird, Cat, Fish, Bug, Feather, Leaf, Droplet, CloudLightning, Orbit, Atom, Infinity, Hash, Triangle, Square, Pentagon, Hexagon, Octagon, Circle, Diamond, ChevronDown, ChevronUp, Phone } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

// Icons mapping for different categories
const categoryIcons = {
  voyance: Eye,
  medium: Moon,
  marabout: Star,
  retourAffectif: Heart,
  sorciere: Wand2,
  guerisseur: Sparkles,
  magieBlanche: Sun,
  magieNoire: Skull,
  magieRouge: Flame,
  rituelAmour: Heart,
  desenvoutement: Shield,
  protection: Shield,
  chance: Gem,
  travail: Target,
  argent: Crown,
  sante: Leaf,
  famille: TreePine,
  couple: Heart,
};

// Generate all SEO articles with Marseille keywords
const generateArticles = () => {
  const locations = [
    'Marseille', 'Marseille 1er', 'Marseille 2ème', 'Marseille 3ème', 'Marseille 4ème',
    'Marseille 5ème', 'Marseille 6ème', 'Marseille 7ème', 'Marseille 8ème', 'Marseille 9ème',
    'Marseille 10ème', 'Marseille 11ème', 'Marseille 12ème', 'Marseille 13ème', 'Marseille 14ème',
    'Marseille 15ème', 'Marseille 16ème', 'Aix-en-Provence', 'Aubagne', 'La Ciotat',
    'Martigues', 'Salon-de-Provence', 'Arles', 'Vitrolles', 'Istres',
    'Cassis', 'La Valentine', 'La Timone', 'Le Prado', 'Castellane',
    'Vieux-Port', 'La Canebière', 'Noailles', 'Belsunce', 'Saint-Charles',
    'La Joliette', 'Euroméditerranée', 'Les Goudes', 'Callelongue', 'Mazargues',
    'Bonneveine', 'La Pointe Rouge', 'Luminy', 'Les Calanques', 'Saint-Loup',
    'La Blancarde', 'Les Chartreux', 'Longchamp', 'Cinq Avenues', 'La Conception',
    'Bouches-du-Rhône', 'PACA', 'Provence', 'Côte d\'Azur', 'Sud de la France'
  ];

  const services = [
    { name: 'Voyant', icon: 'voyance', category: 'Voyance' },
    { name: 'Voyante', icon: 'voyance', category: 'Voyance' },
    { name: 'Médium', icon: 'medium', category: 'Médiumnité' },
    { name: 'Marabout', icon: 'marabout', category: 'Maraboutage' },
    { name: 'Marabout africain', icon: 'marabout', category: 'Maraboutage' },
    { name: 'Grand marabout', icon: 'marabout', category: 'Maraboutage' },
    { name: 'Sorcier', icon: 'sorciere', category: 'Sorcellerie' },
    { name: 'Sorcière', icon: 'sorciere', category: 'Sorcellerie' },
    { name: 'Guérisseur', icon: 'guerisseur', category: 'Guérison' },
    { name: 'Guérisseuse', icon: 'guerisseur', category: 'Guérison' },
    { name: 'Retour affectif', icon: 'retourAffectif', category: 'Amour' },
    { name: 'Récupérer son ex', icon: 'retourAffectif', category: 'Amour' },
    { name: 'Faire revenir son ex', icon: 'retourAffectif', category: 'Amour' },
    { name: 'Retour de l\'être aimé', icon: 'retourAffectif', category: 'Amour' },
    { name: 'Magie blanche', icon: 'magieBlanche', category: 'Magie' },
    { name: 'Magie noire', icon: 'magieNoire', category: 'Magie' },
    { name: 'Magie rouge', icon: 'magieRouge', category: 'Magie' },
    { name: 'Rituel d\'amour', icon: 'rituelAmour', category: 'Rituels' },
    { name: 'Rituel de retour affectif', icon: 'rituelAmour', category: 'Rituels' },
    { name: 'Envoûtement amoureux', icon: 'rituelAmour', category: 'Rituels' },
    { name: 'Désenvoûtement', icon: 'desenvoutement', category: 'Protection' },
    { name: 'Protection spirituelle', icon: 'protection', category: 'Protection' },
    { name: 'Voyance amour', icon: 'voyance', category: 'Voyance' },
    { name: 'Voyance sentimentale', icon: 'voyance', category: 'Voyance' },
    { name: 'Voyance travail', icon: 'voyance', category: 'Voyance' },
    { name: 'Voyance argent', icon: 'voyance', category: 'Voyance' },
    { name: 'Chance aux jeux', icon: 'chance', category: 'Chance' },
    { name: 'Attirer l\'argent', icon: 'argent', category: 'Prospérité' },
    { name: 'Déblocage de situation', icon: 'protection', category: 'Protection' },
    { name: 'Problèmes de couple', icon: 'couple', category: 'Amour' },
  ];

  const articles = [];
  let id = 1;

  // Generate articles for each service + location combination
  services.forEach(service => {
    // Main locations (more detailed articles)
    const mainLocations = locations.slice(0, 20);
    mainLocations.forEach(location => {
      articles.push({
        id: id++,
        title: `${service.name} ${location}`,
        subtitle: `Spécialiste ${service.category} à ${location}`,
        icon: service.icon,
        category: service.category,
        location: location,
        content: `Notre expert, ${service.name.toLowerCase()} reconnu à ${location}, vous accompagne avec expertise et bienveillance. Spécialiste du ${service.category.toLowerCase()}, il met 30 ans d'expérience à votre service. Résultats garantis en 7 jours. Consultation disponible à ${location} et dans toute la région.`,
        keywords: [service.name, location, service.category, 'Expert', '99% réussite', '7 jours']
      });
    });
  });

  // Additional SEO articles for specific combinations
  const additionalArticles = [
    { title: 'Meilleur voyant Marseille', subtitle: 'Le N°1 de la voyance à Marseille', icon: 'voyance', category: 'Voyance', location: 'Marseille' },
    { title: 'Vrai marabout Marseille', subtitle: 'Marabout sérieux et efficace', icon: 'marabout', category: 'Maraboutage', location: 'Marseille' },
    { title: 'Marabout sérieux Marseille', subtitle: 'Résultats garantis', icon: 'marabout', category: 'Maraboutage', location: 'Marseille' },
    { title: 'Marabout efficace Marseille', subtitle: '99% de réussite', icon: 'marabout', category: 'Maraboutage', location: 'Marseille' },
    { title: 'Marabout compétent Marseille', subtitle: '30 ans d\'expérience', icon: 'marabout', category: 'Maraboutage', location: 'Marseille' },
    { title: 'Marabout reconnu Marseille', subtitle: '15,000 clients satisfaits', icon: 'marabout', category: 'Maraboutage', location: 'Marseille' },
    { title: 'Marabout puissant Marseille', subtitle: 'Rituels ancestraux africains', icon: 'marabout', category: 'Maraboutage', location: 'Marseille' },
    { title: 'Voyant médium Marseille', subtitle: 'Don héréditaire de voyance', icon: 'medium', category: 'Médiumnité', location: 'Marseille' },
    { title: 'Médium sérieux Marseille', subtitle: 'Prédictions vérifiables', icon: 'medium', category: 'Médiumnité', location: 'Marseille' },
    { title: 'Retour affectif rapide Marseille', subtitle: 'Résultats en 7 jours', icon: 'retourAffectif', category: 'Amour', location: 'Marseille' },
    { title: 'Retour affectif garanti Marseille', subtitle: '99% de réussite', icon: 'retourAffectif', category: 'Amour', location: 'Marseille' },
    { title: 'Comment récupérer son ex Marseille', subtitle: 'La méthode de Maître Diamma', icon: 'retourAffectif', category: 'Amour', location: 'Marseille' },
    { title: 'Faire revenir son ex rapidement Marseille', subtitle: '7 jours maximum', icon: 'retourAffectif', category: 'Amour', location: 'Marseille' },
    { title: 'Mon ex me manque Marseille', subtitle: 'Solutions pour le retour', icon: 'retourAffectif', category: 'Amour', location: 'Marseille' },
    { title: 'Reconquérir son ex Marseille', subtitle: 'Rituels efficaces', icon: 'retourAffectif', category: 'Amour', location: 'Marseille' },
    { title: 'Rituel retour affectif Marseille', subtitle: 'Magie ancestrale africaine', icon: 'rituelAmour', category: 'Rituels', location: 'Marseille' },
    { title: 'Envoûtement d\'amour Marseille', subtitle: 'Attirer l\'être aimé', icon: 'rituelAmour', category: 'Rituels', location: 'Marseille' },
    { title: 'Sort d\'amour Marseille', subtitle: 'Magie rouge puissante', icon: 'magieRouge', category: 'Magie', location: 'Marseille' },
    { title: 'Philtre d\'amour Marseille', subtitle: 'Recettes ancestrales', icon: 'magieRouge', category: 'Magie', location: 'Marseille' },
    { title: 'Magie blanche amour Marseille', subtitle: 'Rituels bienveillants', icon: 'magieBlanche', category: 'Magie', location: 'Marseille' },
    { title: 'Magie noire amour Marseille', subtitle: 'Rituels puissants', icon: 'magieNoire', category: 'Magie', location: 'Marseille' },
    { title: 'Magie rouge passion Marseille', subtitle: 'Raviver la flamme', icon: 'magieRouge', category: 'Magie', location: 'Marseille' },
    { title: 'Désenvoûtement amoureux Marseille', subtitle: 'Libération des blocages', icon: 'desenvoutement', category: 'Protection', location: 'Marseille' },
    { title: 'Désenvoutement Marseille', subtitle: 'Purification complète', icon: 'desenvoutement', category: 'Protection', location: 'Marseille' },
    { title: 'Protection contre mauvais œil Marseille', subtitle: 'Rituels de protection', icon: 'protection', category: 'Protection', location: 'Marseille' },
    { title: 'Enlever mauvais sort Marseille', subtitle: 'Désenvoûtement efficace', icon: 'desenvoutement', category: 'Protection', location: 'Marseille' },
    { title: 'Guérisseur spirituel Marseille', subtitle: 'Soins énergétiques', icon: 'guerisseur', category: 'Guérison', location: 'Marseille' },
    { title: 'Soins énergétiques Marseille', subtitle: 'Rééquilibrage des chakras', icon: 'guerisseur', category: 'Guérison', location: 'Marseille' },
    { title: 'Voyance téléphone Marseille', subtitle: 'Consultation à distance', icon: 'voyance', category: 'Voyance', location: 'Marseille' },
    { title: 'Voyance en ligne Marseille', subtitle: 'Disponible 7j/7', icon: 'voyance', category: 'Voyance', location: 'Marseille' },
    { title: 'Consultation voyance Marseille', subtitle: 'Première consultation offerte', icon: 'voyance', category: 'Voyance', location: 'Marseille' },
    { title: 'Tarologue Marseille', subtitle: 'Lecture du tarot', icon: 'voyance', category: 'Voyance', location: 'Marseille' },
    { title: 'Cartomancienne Marseille', subtitle: 'Tirage des cartes', icon: 'voyance', category: 'Voyance', location: 'Marseille' },
    { title: 'Astrologue Marseille', subtitle: 'Thème astral complet', icon: 'voyance', category: 'Voyance', location: 'Marseille' },
    { title: 'Numérologue Marseille', subtitle: 'Analyse numérologique', icon: 'voyance', category: 'Voyance', location: 'Marseille' },
    { title: 'Chance aux jeux Marseille', subtitle: 'Rituels de chance', icon: 'chance', category: 'Chance', location: 'Marseille' },
    { title: 'Attirer l\'argent Marseille', subtitle: 'Prospérité financière', icon: 'argent', category: 'Prospérité', location: 'Marseille' },
    { title: 'Déblocage financier Marseille', subtitle: 'Lever les obstacles', icon: 'argent', category: 'Prospérité', location: 'Marseille' },
    { title: 'Réussite professionnelle Marseille', subtitle: 'Carrière et travail', icon: 'travail', category: 'Travail', location: 'Marseille' },
    { title: 'Trouver du travail Marseille', subtitle: 'Aide à l\'emploi', icon: 'travail', category: 'Travail', location: 'Marseille' },
    { title: 'Problèmes de couple Marseille', subtitle: 'Réconciliation', icon: 'couple', category: 'Amour', location: 'Marseille' },
    { title: 'Sauver son couple Marseille', subtitle: 'Éviter la séparation', icon: 'couple', category: 'Amour', location: 'Marseille' },
    { title: 'Crise de couple Marseille', subtitle: 'Solutions rapides', icon: 'couple', category: 'Amour', location: 'Marseille' },
    { title: 'Infidélité Marseille', subtitle: 'Retrouver la confiance', icon: 'couple', category: 'Amour', location: 'Marseille' },
    { title: 'Jalousie maladive Marseille', subtitle: 'Traitement spirituel', icon: 'couple', category: 'Amour', location: 'Marseille' },
    { title: 'Communication avec les esprits Marseille', subtitle: 'Médiumnité pure', icon: 'medium', category: 'Médiumnité', location: 'Marseille' },
    { title: 'Contact défunts Marseille', subtitle: 'Messages de l\'au-delà', icon: 'medium', category: 'Médiumnité', location: 'Marseille' },
    { title: 'Travail occulte Marseille', subtitle: 'Sciences occultes', icon: 'sorciere', category: 'Sorcellerie', location: 'Marseille' },
    { title: 'Marabout africain sérieux Marseille', subtitle: 'Tradition ancestrale', icon: 'marabout', category: 'Maraboutage', location: 'Marseille' },
  ];

  additionalArticles.forEach(article => {
    articles.push({
      id: id++,
      title: article.title,
      subtitle: article.subtitle,
      icon: article.icon,
      category: article.category,
      location: article.location,
      content: `Votre spécialiste en ${article.category.toLowerCase()} à ${article.location}. Fort de 30 ans d'expérience et d'un don héréditaire, il vous accompagne vers la solution. 99% de réussite, résultats en 7 jours. Consultation gratuite au 07 45 44 96 41.`,
      keywords: [article.title, article.location, article.category, 'Expert']
    });
  });

  return articles;
};

export default function BlogSEO() {
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [showAll, setShowAll] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const articles = generateArticles();
  
  // Function to hide "Marseille" visually but keep it for SEO
  const hideMarseille = (text) => {
    if (!text) return text;
    // Replace Marseille and its variants with hidden span version
    return text.split(/(Marseille[^\s]*)/gi).map((part, index) => {
      if (part.toLowerCase().includes('marseille')) {
        return <span key={index} className="seo-hidden">{part}</span>;
      }
      return part;
    });
  };
  
  const categories = ['Tous', 'Amour', 'Voyance', 'Maraboutage', 'Magie', 'Protection', 'Médiumnité', 'Guérison', 'Chance'];

  const filteredArticles = selectedCategory === 'Tous' 
    ? articles 
    : articles.filter(a => a.category === selectedCategory);

  const displayedArticles = showAll ? filteredArticles : filteredArticles.slice(0, 30);

  const getIcon = (iconName) => {
    const IconComponent = categoryIcons[iconName] || Star;
    return IconComponent;
  };

  return (
    <section id="blog" className="py-12 bg-gradient-to-b from-black to-gray-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-green-500/10 border-green-500/30 text-green-500">
            Blog & Articles SEO
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            Guides & Conseils<span className="seo-hidden"> Marseille</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">
            Découvrez nos {articles.length}+ articles sur le <span className="text-green-500">retour affectif</span>, la voyance et les rituels spirituels.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => { setSelectedCategory(cat); setShowAll(false); }}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium ${
                selectedCategory === cat 
                  ? 'bg-green-500 text-black' 
                  : 'bg-gray-800 text-gray-300 border border-gray-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Articles Count */}
        <p className="text-center text-gray-500 text-sm mb-6">
          {filteredArticles.length} articles trouvés
        </p>

        {/* Articles Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayedArticles.map(article => {
            const IconComponent = getIcon(article.icon);
            const isExpanded = expandedId === article.id;
            
            return (
              <Card 
                key={article.id}
                className="p-4 bg-gradient-to-br from-gray-900 to-black border border-gray-800"
              >
                {/* Icon & Category */}
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
                    <IconComponent className="h-5 w-5 text-yellow-500" />
                  </div>
                  <Badge variant="outline" className="text-xs border-gray-700 text-gray-400">
                    {article.category}
                  </Badge>
                </div>

                {/* Title */}
                <h3 className="text-white font-semibold mb-1 text-sm sm:text-base">
                  {hideMarseille(article.title)}
                </h3>
                <p className="text-yellow-500 text-xs mb-2">{hideMarseille(article.subtitle)}</p>

                {/* Content */}
                {isExpanded && (
                  <p className="text-gray-400 text-xs mb-3 leading-relaxed">
                    {hideMarseille(article.content)}
                  </p>
                )}

                {/* Keywords */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {article.keywords.slice(0, 3).map((kw, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 bg-gray-800 text-gray-500 rounded">
                      {hideMarseille(kw)}
                    </span>
                  ))}
                </div>

                {/* Expand Button */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : article.id)}
                  className="text-green-500 text-xs flex items-center gap-1"
                >
                  {isExpanded ? (
                    <>Réduire <ChevronUp className="h-3 w-3" /></>
                  ) : (
                    <>Lire plus <ChevronDown className="h-3 w-3" /></>
                  )}
                </button>
              </Card>
            );
          })}
        </div>

        {/* Show More Button */}
        {!showAll && filteredArticles.length > 30 && (
          <div className="text-center mt-8">
            <Button 
              onClick={() => setShowAll(true)}
              size="lg"
              className="bg-gradient-to-r from-green-500 to-green-600 text-black font-bold px-8 py-6 rounded-full shadow-lg"
            >
              <Star className="h-5 w-5 mr-2" />
              Voir {filteredArticles.length - 30} articles de plus
            </Button>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-12 text-center bg-gradient-to-br from-gray-900 to-black border border-green-500/30 rounded-2xl p-6 sm:p-8">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">
            Besoin d'aide personnalisée ?
          </h3>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto text-sm sm:text-base">
            Consultez <strong className="text-green-500">notre expert</strong> pour un accompagnement sur mesure.
          </p>
          <Button asChild size="lg" className="bg-green-500 text-black font-bold">
            <a href="tel:+33745449641" className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              07 45 44 96 41
            </a>
          </Button>
        </div>

        {/* Hidden SEO Content */}
        <div className="seo-hidden">
          {articles.map(a => (
            <div key={a.id}>
              <h4>{a.title}</h4>
              <p>{a.content}</p>
              {a.keywords.map((k, i) => <span key={i}>{k}</span>)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
